import React from 'react';
import { ArrowLeft, Trash2, MapPin } from 'lucide-react';
import { PreviousDate } from '../types';
import { StorageService } from '../services/storage';

interface HistoryProps {
  dates: PreviousDate[];
  onBack: () => void;
}

export default function History({ dates, onBack }: HistoryProps) {
  const handleDelete = (id: string) => {
    // In real app, would delete from storage
    alert('Date removed from history');
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-4 flex items-center gap-3 z-10">
        <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg">
          <ArrowLeft size={24} className="text-slate-600" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-slate-900">Our Dates</h1>
          <p className="text-xs text-slate-500">{dates.length} memories together ❤️</p>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pb-32">
        {dates.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-96 text-center">
            <div className="text-6xl mb-4">📅</div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">No dates yet</h2>
            <p className="text-slate-600">
              Plan your first date together and it will appear here!
            </p>
          </div>
        ) : (
          <div className="space-y-4 pt-4">
            {dates.map((date) => (
              <div key={date.id} className="card">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-500">
                      📅 {formatDate(date.date)}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">
                      {date.title}
                    </h3>
                  </div>
                  <button
                    onClick={() => handleDelete(date.id)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <Trash2 size={18} className="text-slate-400 hover:text-slate-600" />
                  </button>
                </div>

                {/* Stops */}
                <div className="space-y-2 mb-3">
                  {date.stops.slice(0, 3).map((stop, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                      <MapPin size={14} className="text-rose-600 flex-shrink-0" />
                      <span>{stop}</span>
                    </div>
                  ))}
                  {date.stops.length > 3 && (
                    <div className="text-sm text-slate-500 ml-6">
                      +{date.stops.length - 3} more places
                    </div>
                  )}
                </div>

                {/* Feedback */}
                {date.feedback && (
                  <div className="py-2 px-3 bg-rose-50 border border-rose-200 rounded-lg text-sm">
                    <span className="font-semibold text-rose-900">
                      {date.feedback === 'loved' && '❤️ You loved it!'}
                      {date.feedback === 'good' && '🙂 That was good'}
                      {date.feedback === 'average' && '😐 It was okay'}
                      {date.feedback === 'not-for-us' && '🙃 Not your style'}
                    </span>
                  </div>
                )}

                {/* CTA */}
                <button className="mt-3 w-full py-2 bg-slate-100 text-slate-900 rounded-lg hover:bg-slate-200 text-sm font-medium">
                  View Details
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
